/**
 * UserRegistration - New user registration flow
 * Supports email/password and passkey registration
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Key, Shield, ArrowRight, Check, Loader2 } from "lucide-react";
import { authService } from "@/services/authService";
import { apiKeysService } from "@/services/apiKeysService";
import { useDashboardStore } from "@/store";
import { cn } from "@/lib/utils";
import type { APIKeyPermission } from "@/types";

interface RegistrationData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
}

type Step = "email" | "password" | "confirm" | "passkey" | "success";

export function UserRegistration({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [step, setStep] = useState<Step>("email");
  const [method, setMethod] = useState<"password" | "passkey">("password");
  const [data, setData] = useState<RegistrationData>({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [createdKey, setCreatedKey] = useState<{ key: string; name: string } | null>(null);
  const { addLogEntry } = useDashboardStore();
  
  async function handlePasswordRegister() {
    if (!data.email || !data.name || !data.password) {
      setError("Please fill in all fields");
      return;
    }
    
    if (data.password !== data.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    if (data.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    
    setIsLoading(true);
    setError("");

    try {
      // Simulate registration - in real app this would call Convex
      const result = await authService.register({
        email: data.email,
        password: data.password,
        name: data.name,
      });
      
      if (result.success) {
        addLogEntry({
          category: "auth",
          action: "User Registration",
          details: `New user registered: ${data.email}`,
          severity: "info",
          source: "user-registration",
        });

        // Create initial API key for user
        const { key, keyData } = apiKeysService.generateKey({
          name: `${data.name}'s Key`,
          permissions: ["read", "write"],
        });

        setCreatedKey({ key, name: keyData.name });
        setStep("success");
      } else {
        setError(result.error || "Registration failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasskeyRegister() {
    setIsLoading(true);
    setError("");

    try {
      const result = await authService.registerPasskey(data.name || "New User");

      if (result.success) {
        addLogEntry({
          category: "auth",
          action: "User Registration",
          details: `New user registered via passkey`,
          severity: "info",
          source: "user-registration",
        });

        setStep("success");
      } else {
        setError(result.error || "Passkey registration failed");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey registration failed");
    } finally {
      setIsLoading(false);
    }
  }

  function handleCopyKey() {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey.key);
      addLogEntry({
        category: "auth",
        action: "API Key Copied",
        details: `User copied their API key`,
        severity: "info",
        source: "user-registration",
      });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-lg mx-4"
      >
        <div className="rounded-xl border bg-[hsl(var(--background))] shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="border-b border-[hsl(var(--border))] p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Create Account</h2>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                  Join Binetic to access AGI capabilities
                </p>
              </div>
              <button
                onClick={onCancel}
                className="p-2 rounded-lg hover:bg-[hsl(var(--muted))] cursor-pointer"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Method Selection */}
          {step === "email" && (
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => { setMethod("password"); setStep("password"); }}
                  className={cn(
                    "flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all",
                    method === "password"
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.1)]"
                      : "border-[hsl(var(--border))] hover:border-[hsl(var(--muted))]"
                  )}
                >
                  <Mail className="h-8 w-8" />
                  <span className="font-medium">Email & Password</span>
                </button>
                <button
                  onClick={() => { setMethod("passkey"); setStep("passkey"); }}
                  className={cn(
                    "flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all",
                    method === "passkey"
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.1)]"
                      : "border-[hsl(var(--border))] hover:border-[hsl(var(--muted))]"
                  )}
                >
                  <Key className="h-8 w-8" />
                  <span className="font-medium">Passkey</span>
                </button>
              </div>

              <p className="text-sm text-[hsl(var(--muted-foreground))] text-center">
                Choose your preferred authentication method
              </p>
            </div>
          )}

          {/* Password Registration */}
          <AnimatePresence mode="wait">
            {step === "password" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6 space-y-4"
              >
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Display Name</label>
                    <input
                      type="text"
                      value={data.name}
                      onChange={(e) => setData({ ...data, name: e.target.value })}
                      placeholder="John Doe"
                      className="mt-1.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 focus:border-[hsl(var(--primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <input
                      type="email"
                      value={data.email}
                      onChange={(e) => setData({ ...data, email: e.target.value })}
                      placeholder="john@example.com"
                      className="mt-1.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 focus:border-[hsl(var(--primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Password</label>
                    <input
                      type="password"
                      value={data.password}
                      onChange={(e) => setData({ ...data, password: e.target.value })}
                      placeholder="•••••••"
                      className="mt-1.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 focus:border-[hsl(var(--primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Confirm Password</label>
                    <input
                      type="password"
                      value={data.confirmPassword}
                      onChange={(e) => setData({ ...data, confirmPassword: e.target.value })}
                      placeholder="•••••••"
                      className="mt-1.5 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 focus:border-[hsl(var(--primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-[hsl(var(--destructive)/.1)] text-[hsl(var(--destructive))] text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep("email")}
                    className="flex-1 px-4 py-2 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    onClick={handlePasswordRegister}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-black font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {step === "passkey" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-6 text-center space-y-4"
              >
                <div className="flex justify-center">
                  <Shield className="h-16 w-16 text-[hsl(var(--primary))]" />
                </div>
                <div>
                  <h3 className="font-semibold">Create Account with Passkey</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
                    Use your device's biometric authentication or security key to create your account.
                  </p>
                </div>
                <div>
                  <input
                    type="text"
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    placeholder="Display Name"
                    className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 focus:border-[hsl(var(--primary))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-[hsl(var(--destructive)/.1)] text-[hsl(var(--destructive))] text-sm">
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep("email")}
                    className="flex-1 px-4 py-2 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer"
                  >
                    Back
                  </button>
                  <button
                    onClick={handlePasskeyRegister}
                    disabled={isLoading || !data.name}
                    className="flex-1 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-black font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      <>
                        Register Passkey
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {step === "success" && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="p-6 text-center space-y-6"
              >
                <div className="flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                    <Check className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold">Account Created Successfully!</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    Welcome to Binetic! You can now start using AGI capabilities.
                  </p>
                </div>

                {createdKey && method === "password" && (
                  <div className="space-y-3 p-4 rounded-lg bg-[hsl(var(--muted))]">
                    <div className="text-left">
                      <p className="text-sm font-medium mb-1">Your API Key</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        Save this key securely. You won't see it again.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <code className="flex-1 text-sm bg-[hsl(var(--background))] px-3 py-2 rounded border border-[hsl(var(--border))] font-mono">
                        {createdKey.key}
                      </code>
                      <button
                        onClick={handleCopyKey}
                        className="px-3 py-2 rounded-lg bg-[hsl(var(--primary))] text-black hover:opacity-90 transition-opacity cursor-pointer"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={onSuccess}
                  className="w-full px-4 py-3 rounded-lg bg-[hsl(var(--primary))] text-black font-medium hover:opacity-90 transition-opacity cursor-pointer"
                >
                  Get Started
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
